import React, { useState, useEffect } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { French } from "flatpickr/dist/l10n/fr.js";
import { Editor } from "@tinymce/tinymce-react";
import { cleanHtml, replaceSmartQuotes } from "../../utils/procurementForm.utils";
import { str2bool } from "../../../../helpers/formatHelper";

export default function ProcurementAdditionalInfoSection({
  showInformationsComplementaires,
  typeFacturation,
  setValue,
  watch,
  values,
  errors,
  register,
  fields,
  append,
  remove,
  canAddService,
  services,
  criteriaSelection,
  onCleanedServicesChange,
  onCleanedCriteriaChange,
}) {
  const [cleanedContents, setCleanedContents] = useState(services.map(service => service.description));

  useEffect(() => {
    setCleanedContents((prev) => {
      if (!services) return [];
      if (services.length > prev.length) {
        return [...prev, ...Array(services.length - prev.length).fill("")];
      } else if (services.length < prev.length) {
        return prev.slice(0, services.length);
      }
      return prev;
    });
  }, [services, services.length]);

  useEffect(() => {
    if (typeof onCleanedServicesChange === "function") {
      onCleanedServicesChange(cleanedContents);
    }
  }, [cleanedContents, onCleanedServicesChange]);

  const [criteriaList, setCriteriaList] = useState(criteriaSelection);
  const [cleanedCriteria, setCleanedCriteria] = useState([""]);

  useEffect(() => {
    if (!watch("criteriaSelection")) {
      setCriteriaList([{ detail: "" }]);
      setCleanedCriteria([""]);
    }
  }, [watch]);

  useEffect(() => {
    if (typeof onCleanedCriteriaChange === "function") {
      onCleanedCriteriaChange(cleanedCriteria);
    }
  }, [cleanedCriteria, onCleanedCriteriaChange]);

  const canAddCriteria
    = cleanedCriteria.length > 0
    && cleanedCriteria.every(
      (c, idx) =>
        (c && c.replace(/<(.|\n)*?>/g, "").trim() !== "")
        || idx !== cleanedCriteria.length - 1,
    )
    && cleanedCriteria[cleanedCriteria.length - 1] !== "";

  const handleAddCriteria = () => {
    if (
      cleanedCriteria[cleanedCriteria.length - 1]
      && cleanedCriteria[cleanedCriteria.length - 1].replace(/<(.|\n)*?>/g, "").trim() !== ""
    ) {
      setCriteriaList([...criteriaList, { detail: "" }]);
      setCleanedCriteria([...cleanedCriteria, ""]);
    }
  };

  const handleRemoveCriteria = (idx) => {
    if (criteriaList.length > 1) {
      setCriteriaList(criteriaList.filter((_, i) => i !== idx));
      setCleanedCriteria(cleanedCriteria.filter((_, i) => i !== idx));
    }
  };

  const handleCriteriaEditorChange = (content, idx) => {
    const newList = [...criteriaList];
    newList[idx].detail = content;
    setCriteriaList(newList);

    const cleaned = cleanHtml(content);
    const cleanedList = [...cleanedCriteria];
    cleanedList[idx] = cleaned;
    setCleanedCriteria(cleanedList);

    setValue(`criteria.${idx}.detail`, content, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  if (!showInformationsComplementaires) return null;

  return (
    <>
      <fieldset className="border border-gray-300 p-6 rounded">
        <legend className="block text-lg font-semibold bg-blue-100 px-4 py-2 leading-[1.6] rounded">
          Informations complémentaires
        </legend>

        {/*  PLUSIEURS TRANCHES  */}
        <div className="mt-6">
          <span className="text-sm font-medium block mb-0">
            Votre marché comporte-t-il des tranches ?{" "}
            <span className="text-red-500">*</span>
          </span>
          <div className="flex gap-3 items-center -mt-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="true"
                {...register("hasTranches", {
                  required: "Ce champ est obligatoire",
                })}
              />
              Oui
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="false"
                {...register("hasTranches", {
                  required: "Ce champ est obligatoire",
                })}
                onChange={(e) => {
                  setValue("hasTranches", e.target.value);
                  if (!str2bool(e.target.value)) {
                    setValue("firstTrancheAmountVAT", null);
                    setValue("firstTrancheStartAt", null);
                    setValue("firstTrancheEndAt", null);
                    setValue("secondTrancheAmountVAT", null);
                    setValue("secondTrancheStartAt", null);
                    setValue("secondTrancheEndAt", null);
                  }
                }}
              />
              Non
            </label>
          </div>
          {errors.hasTranches && (
            <p className="text-red-500 text-sm mt-1">
              {errors.hasTranches.message}
            </p>
          )}
        </div>
        {str2bool(watch("hasTranches")) === true && (
          <>
            <div className="mt-2 flex gap-6 items-start">
              <label className="flex-[1.2] block font-medium">
                <span className="text-sm">
                  Montant TVAC de la 1ère tranche <span className="text-red-500">*</span>
                </span>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={values.firstTrancheAmountVAT}
                    onChange={(e) => {
                      const raw = (e.currentTarget.value || "").trim();
                      if (!raw) {
                        setValue("firstTrancheAmountVAT", null, { shouldValidate: true, shouldDirty: true });
                        return;
                      }
                      setValue("firstTrancheAmountVAT", raw.replaceAll(',', '.').replaceAll(' ', ''), { shouldValidate: true, shouldDirty: true });
                    }}

                    className={`mt-1 w-full border rounded px-3 py-2 pr-10 ${errors.firstTrancheAmountVAT ? "border-red-500" : "border-gray-300"}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                    €
                  </span>
                </div>
                <div className="mt-1">
                  {errors.firstTrancheAmountVAT && (
                    <p className="text-red-500 text-sm">{errors.firstTrancheAmountVAT.message}</p>
                  )}
                </div>
              </label>

              <div className="w-1/2 flex flex-col min-h-[96px]">
                <label className="block text-sm font-medium">
                  Date de fin de la 1ère tranche{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative mt-1">
                  <Flatpickr
                    value={values.firstTrancheEndAt || ""}
                    placeholder="jj-mm-yyyy"
                    options={{
                      enableTime: false,
                      dateFormat: "d-m-Y",
                      locale: French,
                      minDate: values.firstTrancheStartAt,
                      utc: true,
                    }}
                    onClose={([date]) => {
                      date.setHours(date.getHours() + 10); // fix timezone
                      setValue("firstTrancheEndAt", date, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    className={`w-full border rounded px-10 py-2 pr-10 ${errors.firstTrancheEndAt
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    style={{ paddingLeft: '2.5rem' }}
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  {values.firstTrancheEndAt && (
                    <button
                      type="button"
                      onClick={() => setValue("firstTrancheEndAt", "")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                {errors.firstTrancheEndAt && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.firstTrancheEndAt.message}
                  </p>
                )}
              </div>
            </div>

            <label className="flex-[1.2] block font-medium">
              <span className="text-sm">
                Montant TVAC de la 2ème tranche
              </span>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  value={values.secondTrancheAmountVAT}
                  onChange={(e) => {
                    const raw = (e.currentTarget.value || "").trim();
                    if (!raw) {
                      setValue("secondTrancheAmountVAT", null, { shouldValidate: true, shouldDirty: true });
                      return;
                    }
                    setValue("secondTrancheAmountVAT", raw.replaceAll(',', '.').replaceAll(' ', ''), { shouldValidate: true, shouldDirty: true });
                  }}

                  className={`mt-1 w-full border rounded px-3 py-2 pr-10 ${errors.secondTrancheAmountVAT ? "border-red-500" : "border-gray-300"}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                  €
                </span>
              </div>
              <div className="mt-1">
                {errors.secondTrancheAmountVAT && (
                  <p className="text-red-500 text-sm">{errors.secondTrancheAmountVAT.message}</p>
                )}
              </div>
            </label>

            <div className="mt-2 flex gap-6 items-start">
              <div className="w-1/2 flex flex-col min-h-[96px]">
                <label className="block text-sm font-medium">
                  Date de début de la 2ème tranche
                </label>
                <div className="relative mt-1">
                  <Flatpickr
                    value={values.secondTrancheStartAt || ""}
                    placeholder="jj-mm-yyyy"
                    options={{
                      enableTime: false,
                      dateFormat: "d-m-Y",
                      time_24hr: true,
                      locale: French,
                    }}
                    onClose={([date]) => {
                      date.setHours(date.getHours() + 10); // fix timezone
                      setValue("secondTrancheStartAt", date, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    className={`w-full border rounded px-10 py-2 pr-10 ${errors.secondTrancheStartAt
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    style={{ paddingLeft: '2.5rem' }}
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  {values.secondTrancheStartAt && (
                    <button
                      type="button"
                      onClick={() => setValue("secondTrancheStartAt", "")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                {errors.secondTrancheStartAt && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.secondTrancheStartAt.message}
                  </p>
                )}
              </div>

              <div className="w-1/2 flex flex-col min-h-[96px]">
                <label className="block text-sm font-medium">
                  Date de fin de la 2ème tranche
                </label>
                <div className="relative mt-1">
                  <Flatpickr
                    value={values.secondTrancheEndAt || ""}
                    placeholder="jj-mm-yyyy"
                    options={{
                      enableTime: false,
                      dateFormat: "d-m-Y",
                      locale: French,
                      minDate: values.secondTrancheStartAt,
                      utc: true,
                    }}
                    onClose={([date]) => {
                      date.setHours(date.getHours() + 10); // fix timezone
                      setValue("secondTrancheEndAt", date, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    className={`w-full border rounded px-10 py-2 pr-10 ${errors.secondTrancheEndAt
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    style={{ paddingLeft: '2.5rem' }}
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  {values.secondTrancheEndAt && (
                    <button
                      type="button"
                      onClick={() => setValue("secondTrancheEndAt", "")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                {errors.secondTrancheEndAt && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.secondTrancheEndAt.message}
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/*  SOUS-TRAITANCE  */}
        <div className="mt-6">
          <span className="text-sm font-medium block mb-0">
            Votre marché peut-il être sous-traité ?{" "}
            <span className="text-red-500">*</span>
          </span>
          <div className="flex gap-3 items-center -mt-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="true"
                {...register("canSubcontracting", {
                  required: "Ce champ est obligatoire",
                })}
              />
              Oui
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="false"
                {...register("canSubcontracting", {
                  required: "Ce champ est obligatoire",
                })}
              />
              Non
            </label>
          </div>
          {errors.canSubcontracting && (
            <p className="text-red-500 text-sm mt-1">
              {errors.canSubcontracting.message}
            </p>
          )}
        </div>

        {/*  TYPE FACTURATION  */}
        <div className="mt-6">
          <span className="text-sm font-medium block mb-0">
            Quel type de facturation est appliqué à votre marché ?{" "}
            <span className="text-red-500">*</span>
          </span>
          <div className="flex gap-6 items-start -mt-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="Mensuelle"
                {...register("TypeFacturation", {
                  required: "Ce champ est obligatoire",
                })}
              />
              Mensuelle
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="Annuelle"
                {...register("TypeFacturation", {
                  required: "Ce champ est obligatoire",
                })}
              />
              Annuelle
            </label>
            <label className="flex flex-col items-center gap-1">
              <input
                type="radio"
                value="Autre"
                {...register("TypeFacturation", {
                  required: "Ce champ est obligatoire",
                })}
              />
              <span>Autre à préciser</span>
            </label>
          </div>
          {errors.TypeFacturation && (
            <p className="text-red-500 text-sm mt-1">
              {errors.TypeFacturation.message}
            </p>
          )}
          {typeFacturation === "Autre" && (
            <label className="block mt-4">
              <span className="text-sm font-medium">
                Veuillez préciser le type de facturation
              </span>
              <input
                type="text"
                placeholder="Ex: Trimestrielle, À la livraison, etc."
                {...register("TypeFacturationAutre", {
                  required: "Ce champ est requis si 'Autre' est sélectionné",
                })}
                className={`mt-1 w-full border rounded px-3 py-2 ${errors.TypeFacturationAutre
                  ? "border-red-500"
                  : "border-gray-300"
                  }`}
              />
              {errors.TypeFacturationAutre && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.TypeFacturationAutre.message}
                </p>
              )}
            </label>
          )}
        </div>

        {/*  SERVICES A FOURNIR  */}
        <span className="text-sm font-medium block mb-4 mt-6">
          Services à inclure dans ce marché{" "}
          <span className="text-red-500">*</span>
        </span>
        <fieldset className="border border-gray-300 rounded-md p-4">
          {fields.map((item, index) => (
            <div
              key={item.id}
              className="mb-4 border rounded p-4 relative bg-white"
            >
              <h4 className="font-medium mb-2 text-gray-700">
                Service {index + 1}
              </h4>
              <label className="block mb-2">
                <span className="text-sm">Nom du service</span>
                <input
                  type="text"
                  {...register(`services.${index}.name`, {
                    required: showInformationsComplementaires
                      ? "Nom requis"
                      : false,
                  })}
                  className={`mt-1 w-full border rounded px-3 py-2 ${errors.services?.[index]?.name
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                />
                {errors.services?.[index]?.name && (
                  <p className="text-red-500 text-sm">
                    {errors.services[index].name.message}
                  </p>
                )}
              </label>
              <label className="block mb-2">
                <span className="text-sm">Détail du service</span>
                <Editor
                  apiKey="tdmkfcmx36wjtguc5w8kcc91k37keduhkcroi36btoigb6wp"
                  value={services[index]?.description || ""}
                  onEditorChange={(newContent) => {
                    setValue(`services.${index}.description`, newContent, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setCleanedContents((prev) => {
                      const copy = [...prev];
                      copy[index] = cleanHtml(newContent);
                      return copy;
                    });
                  }}
                  init={{
                    height: 320,
                    menubar: false,
                    plugins: "link visualblocks lists image",
                    toolbar: "bold italic underline strikethrough | link image | bullist numlist | removeformat",
                    toolbar_mode: "sliding",
                    paste_as_text: false,
                    paste_remove_styles: true,
                    paste_webkit_styles: '',
                    width: '100%',
                    language: 'fr_FR',
                    statusbar: true,
                    branding: false,
                    elementpath: false,
                    resize: true,
                    placeholder: 'Détail du service...',
                    image_title: false,
                    automatic_uploads: true,
                    file_picker_types: 'image',
                    file_picker_callback: (cb, value, meta) => {
                      if (meta.filetype === 'image') {
                        const input = document.createElement('input');
                        input.setAttribute('type', 'file');
                        input.setAttribute('accept', 'image/*');
                        input.onchange = function () {
                          const file = this.files[0];
                          const reader = new FileReader();
                          reader.onload = function () {
                            cb(reader.result, { alt: '' });
                          };
                          reader.readAsDataURL(file);
                        };
                        input.click();
                      }
                    },
                    setup: (editor) => {
                      editor.on('PastePreProcess', (e) => {
                        let html = e.content;
                        html = replaceSmartQuotes(html);
                        e.content = html;
                      });
                    },
                  }}
                />
                {(!services[index]?.description
                  || services[index]?.description.replace(/<(.|\n)*?>/g, "").trim()
                  === "") && (
                    <p className="text-red-500 text-sm">
                      Le détail du service est obligatoire
                    </p>
                  )}
              </label>
              {fields.length > 1 && (
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => remove(index)}
                    className="bg-red-100 text-red-800"
                    style={{
                      border: "none",
                      padding: "4px 18px",
                      cursor: "pointer",
                      borderRadius: 15,
                      fontSize: 13,
                      fontWeight: "bold",
                    }}
                    type="button"
                  >
                    Supprimer ce service
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => append({ nom: "", details: "" })}
            disabled={!canAddService}
            style={{
              marginTop: 16,
              padding: "6px 12px",
              cursor: canAddService ? "pointer" : "not-allowed",
              borderRadius: 4,
              backgroundColor: canAddService ? "#3b82f6" : "#ccc",
              color: "white",
              border: "none",
              fontWeight: "bold",
              fontSize: 14,
              width: "auto",
            }}
            title={
              canAddService
                ? "Ajouter un service"
                : "Complétez le service courant avant d'ajouter"
            }
          >
            + Ajouter un service
          </button>
          {errors.services?.root && (
            <p className="text-red-500 mt-2 text-sm">
              {errors.services.root.message}
            </p>
          )}
        </fieldset>

        {/* CRITÈRES DE SÉLECTION */}
        <div className="mt-6">
          <span className="text-sm font-medium block mb-0">
            Y-a-t-il des critères de sélection ?{" "}
            <span className="text-red-500">*</span>
          </span>
          <div className="flex gap-3 items-center -mt-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="true"
                {...register("criteriaSelection", { required: "Ce champ est obligatoire" })}
                checked={watch("criteriaSelection") === "true"}
                onChange={() => setValue("criteriaSelection", "true")}
              />
              Oui
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="false"
                {...register("criteriaSelection", { required: "Ce champ est obligatoire" })}
                checked={watch("criteriaSelection") === "false"}
                onChange={() => setValue("criteriaSelection", "false")}
              />
              Non
            </label>
          </div>
          {errors.criteriaSelection && (
            <p className="text-red-500 text-sm mt-1">
              {errors.criteriaSelection.message}
            </p>
          )}
        </div>
        {(watch("criteriaSelection") === true || watch("criteriaSelection") === "true") && (
          <>
            <span className="text-sm font-medium block mb-4 mt-6">
              Quels sont ces critères de sélection ?{" "}
              <span className="text-red-500">*</span>
            </span>
            <fieldset className="border border-blue-300 rounded-md p-4 mt-4">
              {criteriaList.map((crit, idx) => (
                <div
                  key={idx}
                  className="mb-4 border rounded p-4 relative bg-white"
                >
                  <h4 className="font-medium mb-2 text-gray-700">
                    Critère {idx + 1}
                  </h4>
                  <label className="block mb-2">
                    <span className="text-sm">Description du critère</span>
                    <Editor
                      apiKey="tdmkfcmx36wjtguc5w8kcc91k37keduhkcroi36btoigb6wp"
                      value={criteriaList[idx]?.detail || ""}
                      onEditorChange={newContent =>
                        handleCriteriaEditorChange(newContent, idx)}
                      init={{
                        height: 320,
                        menubar: false,
                        plugins: "link visualblocks lists image",
                        toolbar:
                          "bold italic underline strikethrough | link image | bullist numlist | removeformat",
                        toolbar_mode: "sliding",
                        paste_as_text: false,
                        paste_remove_styles: true,
                        paste_webkit_styles: "",
                        width: "100%",
                        language: "fr_FR",
                        statusbar: true,
                        branding: false,
                        elementpath: false,
                        resize: true,
                        placeholder: "Décrivez le critère de sélection...",
                        image_title: false,
                        automatic_uploads: true,
                        file_picker_types: "image",
                        file_picker_callback: (cb, value, meta) => {
                          if (meta.filetype === "image") {
                            const input = document.createElement("input");
                            input.setAttribute("type", "file");
                            input.setAttribute("accept", "image/*");
                            input.onchange = function () {
                              const file = this.files[0];
                              const reader = new FileReader();
                              reader.onload = function () {
                                cb(reader.result, { alt: "" });
                              };
                              reader.readAsDataURL(file);
                            };
                            input.click();
                          }
                        },
                        setup: (editor) => {
                          editor.on("PastePreProcess", (e) => {
                            let html = e.content;
                            html = replaceSmartQuotes(html);
                            e.content = html;
                          });
                        },
                      }}
                    />
                    {(!criteriaList[idx]?.detail
                      || criteriaList[idx]?.detail.replace(/<(.|\n)*?>/g, "").trim()
                      === "") && (
                        <p className="text-red-500 text-sm">
                          Ce critère de sélection est obligatoire
                        </p>
                      )}
                  </label>
                  {criteriaList.length > 1 && (
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={() => handleRemoveCriteria(idx)}
                        className="bg-red-100 text-red-800"
                        style={{
                          border: "none",
                          padding: "4px 18px",
                          cursor: "pointer",
                          borderRadius: 15,
                          fontSize: 13,
                          fontWeight: "bold",
                        }}
                        type="button"
                      >
                        Supprimer ce critère
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddCriteria}
                disabled={!canAddCriteria}
                style={{
                  marginTop: 16,
                  padding: "6px 12px",
                  cursor: canAddCriteria ? "pointer" : "not-allowed",
                  borderRadius: 4,
                  backgroundColor: canAddCriteria ? "#3b82f6" : "#ccc",
                  color: "white",
                  border: "none",
                  fontWeight: "bold",
                  fontSize: 14,
                  width: "auto",
                }}
                title={
                  canAddCriteria
                    ? "Ajouter un critère"
                    : "Complétez le critère courant avant d'ajouter"
                }
              >
                + Ajouter un critère
              </button>
              {cleanedCriteria.length === 0 || cleanedCriteria.some(c => !c || c.replace(/<(.|\n)*?>/g, "").trim() === "")
                ? (
                  <p className="text-red-500 mt-2 text-sm">
                    Vous devez remplir au moins un critère
                  </p>
                )
                : null}
            </fieldset>
          </>
        )}

        {/* SÉLECTION QUALITATIVE DES PRESTATAIRES */}
        <div className="mt-6">
          <span className="text-sm font-medium block mb-0">
            Y-a-t-il une sélection qualitative de vos prestataires ?{" "}
            <span className="text-red-500">*</span>
          </span>
          <div className="flex gap-3 items-center -mt-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="true"
                {...register("hasQualitativeSelection", {
                  required: "Ce champ est obligatoire",
                })}
                checked={watch("hasQualitativeSelection") === "true"}
                onChange={() => setValue("hasQualitativeSelection", "true")}
              />
              Oui
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="false"
                {...register("hasQualitativeSelection", {
                  required: "Ce champ est obligatoire",
                })}
                checked={watch("hasQualitativeSelection") === "false"}
                onChange={() => setValue("hasQualitativeSelection", "false")}
              />
              Non
            </label>
          </div>
          {errors.hasQualitativeSelection && (
            <p className="text-red-500 text-sm mt-1">
              {errors.hasQualitativeSelection.message}
            </p>
          )}
        </div>
      </fieldset>
    </>
  );
}
